import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { Send, ChevronRight, RotateCcw } from 'lucide-react';

import { Button, Input } from '@llura/ui';
import { AnalyticsEvents, AnalyticsProps } from '@llura/constants';
import type { ChatMessage, ChatStreamRequest } from '@llura/types';

import { CoachAvatar } from '../../shared/coach-avatar';
import { useChatStore } from '../../stores/chat.store';
import { useCoachStore } from '../../stores/coach.store';
import { useNotificationPrefsStore } from '../../stores/notification-prefs.store';
import { useCovenantStore } from '../../stores/covenant.store';
import { usePersonalityStore } from '../../stores/personality.store';
import { useUiStore } from '../../stores/ui.store';
import { streamChat } from '../../lib/sse';
import { useHaptic } from '../../hooks/useHaptic';
import { useStrings } from '../../hooks/useStrings';
import { trackEvent } from '../../lib/analytics';

interface Message {
  id: string;
  from: 'coach' | 'user';
  text: string;
  streaming?: boolean;
}

function uid() {
  return Math.random().toString(36).slice(2, 10);
}

function TypingDots() {
  return (
    <span
      className="inline-flex items-center gap-1 py-1 align-middle"
      aria-label="typing"
      role="status"
    >
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="block h-1.5 w-1.5 rounded-full bg-secondary/80"
          animate={{ opacity: [0.25, 1, 0.25], y: [0, -1.5, 0] }}
          transition={{
            duration: 1.4,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: i * 0.18,
          }}
        />
      ))}
    </span>
  );
}

export function CoachChatPage() {
  const navigate = useNavigate();
  const haptic = useHaptic();
  const { strings, locale } = useStrings();
  const { name, avatarUrl, tone, gender } = useCoachStore();
  const boundaries = useCovenantStore((s) => s.boundaries);
  const safeWord = useCovenantStore((s) => s.safeWord);
  const personalityNarrative = usePersonalityStore((s) => s.narrative);
  const draft = useUiStore((s) => s.coachChatDraft);
  const setDraft = useUiStore((s) => s.setCoachChatDraft);
  const optInPromptSeen = useNotificationPrefsStore((s) => s.optInPromptSeen);

  // Resume any persisted conversation. The stored shape (id/from/text) is a
  // subset of Message, so it maps straight in — completed turns only, never a
  // half-streamed bubble.
  const [messages, setMessages] = useState<Message[]>(
    () => useChatStore.getState().messages,
  );
  const [streaming, setStreaming] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const didInit = useRef(false);

  const toneLabel = tone ? strings.coach.tones[tone].label : '';

  // Stream one assistant turn into the bubble `id`, retrying once if the stream
  // ends empty or errors. This is a safety net on top of the server-side retry:
  // it covers the rare transient empty-completion that survives all server
  // attempts, plus any network/mid-stream error. Resolves to whether real text
  // was produced so callers can decide how to recover from a total failure.
  const streamReply = useCallback(
    async (request: ChatStreamRequest, id: string): Promise<boolean> => {
      const MAX_TRIES = 2; // initial + 1 retry
      for (let attempt = 1; attempt <= MAX_TRIES; attempt++) {
        let accumulated = '';
        try {
          for await (const delta of streamChat(request)) {
            accumulated += delta;
            setMessages((prev) =>
              prev.map((m) => (m.id === id ? { ...m, text: accumulated } : m)),
            );
          }
        } catch (err) {
          console.error('Chat stream failed', err);
        }
        if (accumulated.trim().length > 0) return true;
        // Empty result: clear the bubble before retrying so a partial retry
        // doesn't concatenate onto stale text.
        setMessages((prev) =>
          prev.map((m) => (m.id === id ? { ...m, text: '' } : m)),
        );
      }
      return false;
    },
    [],
  );

  // Stream a fresh opener from the LLM. Used on a first/empty visit and by the
  // start-over button.
  const generateOpener = useCallback(() => {
    if (!tone || !name || !gender) return;
    const id = uid();
    setMessages([{ id, from: 'coach', text: '', streaming: true }]);
    setStreaming(true);

    const request: ChatStreamRequest = {
      messages: [],
      settings: {
        coach: { name, gender, tone },
        covenant: { boundaries, safeWord },
        locale,
        ...(personalityNarrative ? { personalityNarrative } : {}),
      },
      isOpening: true,
    };

    void (async () => {
      const ok = await streamReply(request, id);
      // Total failure: drop the empty opener bubble so no blank message is left
      // behind. The mount effect / start-over remain the recovery path.
      if (!ok) {
        setMessages((prev) => prev.filter((m) => m.id !== id));
      } else {
        setMessages((prev) =>
          prev.map((m) => (m.id === id ? { ...m, streaming: false } : m)),
        );
      }
      setStreaming(false);
    })();
  }, [
    tone,
    name,
    gender,
    boundaries,
    safeWord,
    locale,
    personalityNarrative,
    streamReply,
  ]);

  // On first mount only: if there's no saved conversation to resume, stream the
  // opener. A resumed conversation is left untouched (no opener, no clobber).
  useEffect(() => {
    if (didInit.current || !tone || !name || !gender) return;
    didInit.current = true;
    if (useChatStore.getState().messages.length === 0) generateOpener();
  }, [tone, name, gender, generateOpener]);

  // Persist completed turns once a turn settles — never mid-stream, so a
  // half-written bubble is dropped rather than saved. One write per turn keeps
  // localStorage churn down vs. writing on every delta.
  useEffect(() => {
    if (streaming) return;
    useChatStore.getState().setMessages(
      messages
        .filter((m) => !m.streaming && m.text.trim().length > 0)
        .map(({ id, from, text }) => ({ id, from, text })),
    );
  }, [streaming, messages]);

  const startOver = () => {
    haptic.tap();
    useChatStore.getState().clear();
    setMessages([]);
    generateOpener();
  };

  // Autoscroll on every message change.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = () => {
    if (!draft.trim() || streaming || !tone || !name || !gender) return;

    const userText = draft.trim();
    const userMsg: Message = { id: uid(), from: 'user', text: userText };

    // Build the conversation we'll ship: every prior turn EXCEPT the opener
    // (messages[0] — the system prompt tells the model not to repeat it) and
    // any in-flight streaming bubble.
    const history: ChatMessage[] = messages
      .slice(1)
      .filter((m) => !m.streaming && m.text.trim().length > 0)
      .map((m) => ({
        role: m.from === 'coach' ? 'assistant' : 'user',
        content: m.text,
      }));

    const request: ChatStreamRequest = {
      messages: [...history, { role: 'user', content: userText }],
      settings: {
        coach: { name, gender, tone },
        covenant: { boundaries, safeWord },
        locale,
        ...(personalityNarrative ? { personalityNarrative } : {}),
      },
    };

    setMessages((prev) => [...prev, userMsg]);
    setDraft('');
    haptic.tap();
    trackEvent(AnalyticsEvents.ChatMessageSent, {
      [AnalyticsProps.Tone]: tone,
      [AnalyticsProps.MessageLength]: userText.length,
    });

    const replyId = uid();
    setStreaming(true);
    setMessages((prev) => [
      ...prev,
      { id: replyId, from: 'coach', text: '', streaming: true },
    ]);

    void (async () => {
      const ok = await streamReply(request, replyId);
      // Total failure: drop the empty reply bubble so the user isn't left with
      // a blank message and can simply resend.
      if (!ok) {
        setMessages((prev) => prev.filter((m) => m.id !== replyId));
      } else {
        setMessages((prev) =>
          prev.map((m) => (m.id === replyId ? { ...m, streaming: false } : m)),
        );
      }
      setStreaming(false);
    })();
  };

  return (
    <div className="flex h-[calc(100svh-6rem)] flex-col bg-background">
      <header className="sticky top-0 z-10 flex items-center gap-3 border-b border-border bg-background/85 px-4 py-3 backdrop-blur">
        <CoachAvatar name={name} src={avatarUrl} size={40} />
        <div className="min-w-0 flex-1">
          <div className="truncate font-medium leading-tight">{name}</div>
          <div className="text-xs text-muted-foreground">{toneLabel}</div>
        </div>
        <Button
          size="icon"
          variant="ghost"
          onClick={startOver}
          disabled={streaming}
          className="h-9 w-9 shrink-0 text-muted-foreground"
          aria-label={strings.onboarding.chat.startOverAria}
        >
          <RotateCcw className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            haptic.success();
            // First time through, offer the push opt-in once; afterwards go
            // straight to the dashboard.
            navigate(optInPromptSeen ? '/dashboard' : '/onboarding/notifications');
          }}
          className="shrink-0 gap-1"
        >
          {strings.onboarding.chat.beginCta}
          <ChevronRight className="h-4 w-4" />
        </Button>
      </header>

      <div
        ref={listRef}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-5 no-scrollbar"
      >
        {messages.map((m) => (
          <motion.div
            key={m.id}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            className={
              m.from === 'coach'
                ? 'flex max-w-[85%] gap-2'
                : 'ml-auto flex max-w-[85%] justify-end'
            }
          >
            {m.from === 'coach' && (
              <CoachAvatar name={name} src={avatarUrl} size={32} className="mt-1 shrink-0" />
            )}
            <div
              className={
                m.from === 'coach'
                  ? `rounded-2xl rounded-tl-sm border border-border bg-card px-3.5 py-2.5 text-sm leading-relaxed${
                      m.streaming && !m.text ? ' shadow-[0_0_28px_-12px_hsl(var(--primary)/0.55)]' : ''
                    }`
                  : 'rounded-2xl rounded-tr-sm bg-primary px-3.5 py-2.5 text-sm leading-relaxed text-primary-foreground'
              }
            >
              {m.streaming && !m.text ? <TypingDots /> : m.text}
            </div>
          </motion.div>
        ))}
      </div>

      <footer className="border-t border-border bg-card/40 px-4 py-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] backdrop-blur">
        <form
          className="flex items-center gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
        >
          <Input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder={strings.onboarding.chat.placeholder}
            className="rounded-full bg-background"
            disabled={streaming}
          />
          <Button
            type="submit"
            size="icon"
            className="h-10 w-10 shrink-0 rounded-full bg-primary glow-primary"
            disabled={streaming || !draft.trim()}
            aria-label={strings.common.sendAria}
          >
            <Send className="h-4 w-4" />
          </Button>
        </form>
      </footer>
    </div>
  );
}
